import { UpdatePayment, UpdatePaymentsInput } from "@/graphql/documentNode"
import Modal from "../common/modal/modal"
import { useCallback, useMemo, useState } from "react"
import { Payment } from "@/graphql/types"
import { useQuery } from "react-query"
import Parallelogram from "../common/commonIcon/parallelogram"
import styled from "styled-components"
import Button from "../common/button/button"
import { TextField } from "@mui/material"
import GuestInput from "../common/input/guestInput"
import { REQUIRED_STRING_REGEXP } from "../reservation"
import * as GraphQLClient from "@apollo/client"
import { useSnackBar } from "@/lib/ts/hooks/useSnackBar"

type Props = {
    productionId: number
    getPaymentsOfProduction: (productionId: number) => Promise<Payment[]>
    updatePayments: (input: UpdatePaymentsInput) => Promise<void>
    onClose: () => void
    device: string
}

const SetupPayment = (props: Props) => {
    const snackBar = useSnackBar({})
    
    const [payments, setPayments] = useState([] as Payment[])
    const [selectedPaymentIndex, setSelectedPaymentIndex] = useState(-1)
    const [showCreateModal, setShowCreateModal] = useState(false)

    const [name, setName] = useState("")
    const [link, setLink] = useState("")
    const [description, setDescription] = useState("")
    const [nameError, setNameError] = useState(false)
    const [linkError, setLinkError] = useState(false)
    const [descriptionError, setDescriptionError] = useState(false)

    const selectedPayment = useMemo(() => {
        return payments[selectedPaymentIndex]
    }, [payments, selectedPaymentIndex])
    
    useQuery([selectedPaymentIndex], () => {
        if(selectedPayment) {
            setName(selectedPayment.name)
            setLink(selectedPayment.link || "")
            setDescription(selectedPayment.description)
            setNameError(false)
            setLinkError(false)
            setDescriptionError(false)
        }
    })

    useQuery([props.productionId], async () => {
        const result = await props.getPaymentsOfProduction(props.productionId)
        setPayments(result)
    })

    const mutatePayment = useCallback(async () => {
        await props.updatePayments({
            payments,
            productionId: props.productionId,
        })
    }, [payments, props])


    const [updatePayment] = GraphQLClient.useMutation(UpdatePayment, {
        onCompleted: (data) => {
            if(data.updatePayment.ok) {
                snackBar.onMutationSuccess()
            } else {
                snackBar.onMutationError()
            }
        },
        onError: (err) => {
            console.log(err)
            snackBar.onMutationError()
        },
    })

    const updateSelectedPayment = useCallback(async () => {
        if(selectedPayment) {
            await updatePayment({
                variables: {
                    input: {
                        id: selectedPayment.id,
                        name,
                        link,
                        description,
                    }
                }
            })
        }
    }, [description, link, name, selectedPayment, updatePayment])

    const deletePayment = useCallback(() => {
        const paymentsCopy = [...payments]
        paymentsCopy.splice(selectedPaymentIndex, 1)
        setPayments(paymentsCopy)
        setSelectedPaymentIndex(-1)
    }, [payments, selectedPaymentIndex])


    const openCreateModal = useCallback(() => {
        setSelectedPaymentIndex(-1)
        setShowCreateModal(true)
    }, [])

    const components = useMemo(() => (
        <>
            <div className="payments">
                <div className="payment-list">
                    {
                        payments.map((payment, index) => (
                            <div key={"payment-" + payment.id} className="payment">
                                <Parallelogram
                                    bodyColor="black"
                                    shadowColor="red"
                                    onClick={() => {
                                        setSelectedPaymentIndex(index)
                                    }}
                                    width={280}
                                >
                                    {payment.name}
                                </Parallelogram>
                            </div>
                        ))
                    }
                </div>
                <div className="buttons">
                    <Button
                        label={"新規作成"}
                        onClick={openCreateModal}
                        buttonType="powerful-red"
                        width={90}
                    />
                    <Button
                        label={"保存"}
                        onClick={mutatePayment}
                        buttonType="powerful-red"
                        width={90}
                    />
                    <Button
                        label={"CLOSE"}
                        onClick={props.onClose}
                        buttonType="powerful-red"
                        width={90}
                    />
                </div>
            </div>
            <div className="selected-box">
                {selectedPayment ? <div>
                    <div className="element-box">
                    <div className="element">
                        <div className="element-name">
                            <Parallelogram
                                bodyColor="black"
                                shadowColor="red"
                                width={280}
                            >
                                支払方法名称
                            </Parallelogram>
                        </div>
                        <div className="element-content">
                            <TextField 
                                defaultValue={selectedPayment.name}
                                key={`selected ${selectedPaymentIndex} name`}
                                variant="standard"
                                onChange={(event) => { setName(event.target.value) }}
                            />
                        </div>
                    </div>
                    <div className="element">
                        <div className="element-name">
                            <Parallelogram
                                bodyColor="black"
                                shadowColor="red"
                                width={280}
                            >
                                支払リンク
                            </Parallelogram>
                        </div>
                        <div className="element-content">
                            <TextField 
                                defaultValue={selectedPayment.link}
                                key={`selected ${selectedPaymentIndex} link`}
                                variant="standard"
                                onChange={(event) => { setLink(event.target.value) }}
                            />
                        </div>
                    </div>
                    <div className="element">
                        <div className="element-name">
                            <Parallelogram
                                bodyColor="black"
                                shadowColor="red"
                                width={280}
                            >
                                説明
                            </Parallelogram>
                        </div>
                        <div className="element-content">
                            <TextField 
                                defaultValue={selectedPayment.description}
                                key={`selected ${selectedPaymentIndex} description`}
                                variant="outlined"
                                rows={5}
                                multiline
                                onChange={(event) => { setDescription(event.target.value) }}
                            />
                        </div>
                    </div>  
                    </div>
                    <div className="buttons">
                        <Button 
                            buttonType="powerful-red"
                            label={"削除"}
                            onClick={deletePayment}
                        />
                        <Button 
                            buttonType="powerful-red"
                            label={"保存"}
                            onClick={updateSelectedPayment}
                        />
                    </div>
                </div> : <></>}
            </div>
        </>
    ), [deletePayment, mutatePayment, openCreateModal, payments, props.onClose, selectedPayment, selectedPaymentIndex, updateSelectedPayment])

    const createPayment = useCallback(() => {
        const paymentsCopy = [...payments]
        paymentsCopy.push({
            name,
            description,
            link,
            productionId: props.productionId,
        })
        setPayments(paymentsCopy)
        setShowCreateModal(false)
    }, [description, link, name, payments, props.productionId])

    const onCloseModal = useCallback(() => {
        setSelectedPaymentIndex(-1)
        setShowCreateModal(false)
    }, [])

    const createModal = useMemo(() => {
        return (
            <Modal
                title="支払方法作成"
                onClose={onCloseModal}
                maxHeight={400}
            >
                <CreateModalStyle className={showCreateModal ? "fixed" : ""}>
                    <div className="element">
                        <div className="element-name">
                            <Parallelogram
                                bodyColor="black"
                                shadowColor="red"
                                width={280}
                            >
                                支払方法名称
                            </Parallelogram>
                            <div className="text-field">
                                <GuestInput 
                                    label=""
                                    placeholder=""
                                    id="name"
                                    defaultValue=""
                                    onChange={(value, error) => {
                                        setName(value)
                                        setNameError(error)
                                    }}
                                    error={nameError}
                                    errorMessage=""
                                    literalValidation={REQUIRED_STRING_REGEXP}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="element">
                        <div className="element-name">
                            <Parallelogram
                                bodyColor="black"
                                shadowColor="red"
                                width={280}
                            >
                                支払リンク
                            </Parallelogram>
                            <div className="text-field">
                                <GuestInput 
                                    label=""
                                    placeholder=""
                                    id="link"
                                    defaultValue=""
                                    onChange={(value, error) => {
                                        setLink(value)
                                        setLinkError(error)
                                    }}
                                    error={linkError}
                                    errorMessage=""
                                    literalValidation={/.*/}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="element">
                        <div className="element-name">
                            <Parallelogram
                                bodyColor="black"
                                shadowColor="red"
                                width={280}
                            >
                                説明
                            </Parallelogram>
                            <div className="text-field">
                                <TextField 
                                    defaultValue=""
                                    variant="outlined"
                                    multiline
                                    rows={4}
                                    sx={{width: '250px'}}
                                    onChange={(event) => { setDescription(event.target.value) }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="buttons">
                        <Button
                            label={"作成"}
                            onClick={createPayment}
                            buttonType="powerful-red"
                            disabled={nameError || descriptionError || linkError}
                        />
                        <Button
                            label={"CLOSE"}
                            onClick={() => {setShowCreateModal(false)}}
                            buttonType="powerful-red"
                        />
                    </div>
                </CreateModalStyle>
            </Modal>
        )
    }, [createPayment, descriptionError, linkError, nameError, onCloseModal, showCreateModal])

    return (
        <>
            <Modal
                title="支払方法設定"
                onClose={props.onClose}
                width={props.device === "PC" ? 650 : 330}
                noScroll={showCreateModal}
            >
                <>
                    {
                        props.device === "PC" ? (
                            <SetupPaymentStylePC>
                                {components}
                            </SetupPaymentStylePC>
                        ) : (
                            <SetupPaymentStyleMobile>
                                {components}
                            </SetupPaymentStyleMobile>
                        )
                    }
                    {
                        showCreateModal ? createModal : <></>
                    }
                </>
            </Modal>
            {snackBar.component}
        </>
    )
}

export default SetupPayment

const CreateModalStyle = styled.div`
    .element {
            margin: 10px auto 10px auto;
            width: 300px;
            height: 100px;
            text-align: center;
            .element-name {
                margin: auto;
                color: white;
                font-weight: bold;
                text-align: center;
            }
            .text-field {
                margin: 20px auto 0 auto;
            }
        }
    .buttons {
        margin-top: 100px;
        display: flex;
        gap: 10px;
        justify-content: center;
    }
`

const SetupPaymentStyleMobile = styled.div`
    .selected-box {
    }
`

const SetupPaymentStylePC = styled.div`
display: flex;
gap: 10px;
    .payments {
        width: 300px;
        height: 400px;
        .payment-list {
            width: 300px;
            height: 350px;
            border-style: solid;
            overflow-y: scroll;
            .payment {
                margin: 20px auto 20px auto;
            }
        }
        .buttons {
            margin: 10px auto 0 auto;
            display: flex;
            justify-content: center;
            gap: 10px;
        }
    }
    .selected-box {
        width: 300px;
        height: 400px;
        border-style: solid;
        padding: 10px 10px 10px 10px;
        text-align: center;
        .element-box {
            overflow-y: scroll;
        }
        .element {
            margin: auto;
            width: 300px;
            height: 100px;
            text-align: center;
            .element-name {
                margin: auto;
                color: white;
                font-weight: bold;
                text-align: center;
            }
            .element-content {
                margin: 20px;
            }
        }

        .buttons {
            margin: 10px auto 0 auto;
            display: flex;
            justify-content: center;
            gap: 10px;
        }
    }
`